import { lazy, Suspense, type ReactNode } from 'react'
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { ArrowUpRight, Briefcase, FolderGit2, MessageSquare, Network } from 'lucide-react'
import Layout from './components/Layout'
import LoginView from './views/LoginView'
import { useChatStore } from './store/chatStore'
import { AuthProvider, useAuth } from './hooks/useAuth'

const ChatView = lazy(() => import('./views/ChatView'))
const SourceKnowledgeView = lazy(() => import('./views/SourceKnowledgeView'))
const KnowledgeView = lazy(() => import('./views/KnowledgeView'))
const KnowledgeCurationView = lazy(() => import('./views/KnowledgeCurationView'))
const DataSourcesView = lazy(() => import('./views/DataSourcesView'))
const LegacyUnderstandingView = lazy(() => import('./views/LegacyUnderstandingView'))
const SkillsView = lazy(() => import('./views/SkillsView'))
const WorkItemsView = lazy(() => import('./views/WorkItemsView'))
const AdminSettingsView = lazy(() => import('./views/AdminSettingsView'))
const AdminProjectsView = lazy(() => import('./views/AdminProjectsView'))
const AdminDatasourcesView = lazy(() => import('./views/AdminDatasourcesView'))
const AdminGatewayView = lazy(() => import('./views/AdminGatewayView'))
const AdminGraphIntelligenceView = lazy(() => import('./views/AdminGraphIntelligenceView'))
const RepoWorkspaceView = lazy(() => import('./views/RepoWorkspaceView'))

function RouteLoading() {
  return <div className="loading">Loading...</div>
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const location = useLocation()
  const { authenticated, loading } = useAuth()

  if (loading) {
    return <div className="loading">Loading...</div>
  }

  if (!authenticated) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
  }

  return <>{children}</>
}

function ForgeTechnicalHandoffView() {
  const { projectId } = useParams()
  const projects = useChatStore(s => s.projects)
  const project = projects.find(p => p.id === projectId)
  const firstRepo = project?.linkedRepositories?.[0]
  const forgeBase = (import.meta.env.VITE_FORGE_URL as string | undefined) || 'https://forge.int10h.de'
  const forgeHref = firstRepo
    ? `${forgeBase.replace(/\/$/, '')}/repositories?project=${encodeURIComponent(projectId || '')}&repo=${encodeURIComponent(firstRepo)}`
    : `${forgeBase.replace(/\/$/, '')}/repositories`

  return (
    <div className="mx-auto flex h-full max-w-5xl flex-col justify-center py-10">
      <section className="border-b border-border pb-8">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md border border-primary/25 bg-primary/10 text-primary">
            <Briefcase className="h-5 w-5" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Contextful is the product workspace</h1>
            <p className="mt-1 text-sm text-muted-foreground">{project?.name ?? 'This project'} keeps product questions, operating context, and decisions here.</p>
          </div>
        </div>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border border-border bg-card p-4">
            <MessageSquare className="h-4 w-4 text-primary" aria-hidden="true" />
            <div className="mt-3 text-sm font-medium text-foreground">Ask product questions</div>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">Use Contextful for product discovery, prioritization, operating context, decisions, and cross-team alignment.</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <Network className="h-4 w-4 text-primary" aria-hidden="true" />
            <div className="mt-3 text-sm font-medium text-foreground">Keep shared context</div>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">Knowledge, sources, work items, and decisions remain connected across tenant and organization boundaries.</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <FolderGit2 className="h-4 w-4 text-primary" aria-hidden="true" />
            <div className="mt-3 text-sm font-medium text-foreground">Open Forge for code</div>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">Technical repository work, architecture chat, branches, pull requests, and code tasks live in Forge.</p>
          </div>
        </div>
      </section>

      <section className="grid gap-6 py-8 md:grid-cols-[1fr_auto] md:items-center">
        <div>
          <div className="text-sm font-medium text-foreground">Continue in the right workspace</div>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {firstRepo
              ? `Forge will open the technical view for ${firstRepo}.`
              : 'Open Forge to connect repositories and start technical work.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-3 md:justify-end">
          <a
            href={projectId ? `/p/${projectId}` : '/'}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition hover:bg-muted/60"
          >
            Back to Contextful chat
          </a>
          <a
            href={forgeHref}
            className="inline-flex items-center justify-center gap-2 rounded-md border border-primary bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition hover:bg-primary/90"
          >
            Open Forge <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </section>
    </div>
  )
}
function AppRoutes() {
  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route path="/login" element={<LoginView />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout>
                <ChatView />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/c/:conversationId"
          element={
            <ProtectedRoute>
              <Layout>
                <ChatView />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/p/:projectId"
          element={
            <ProtectedRoute>
              <Layout>
                <ChatView />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/p/:projectId/knowledge"
          element={
            <ProtectedRoute>
              <Layout>
                <KnowledgeView />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/p/:projectId/knowledge/sources"
          element={<ProtectedRoute><Layout><SourceKnowledgeView /></Layout></ProtectedRoute>}
        />
        <Route
          path="/p/:projectId/knowledge/curation"
          element={
            <ProtectedRoute>
              <Layout>
                <KnowledgeCurationView />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/p/:projectId/skills"
          element={
            <ProtectedRoute>
              <Layout>
                <SkillsView />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/p/:projectId/datasources"
          element={
            <ProtectedRoute>
              <Layout>
                <DataSourcesView />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/p/:projectId/legacy-understanding"
          element={
            <ProtectedRoute>
              <Layout fullWidth>
                <LegacyUnderstandingView />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/p/:projectId/architect"
          element={
            <ProtectedRoute>
              <Layout>
                <ForgeTechnicalHandoffView />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/p/:projectId/architect/code/:repo/*"
          element={
            <ProtectedRoute>
              <Layout>
                <ForgeTechnicalHandoffView />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/p/:projectId/architect/repos"
          element={
            <ProtectedRoute>
              <Layout>
                <ForgeTechnicalHandoffView />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/p/:projectId/architect/spec/:specId?"
          element={
            <ProtectedRoute>
              <Layout>
                <ForgeTechnicalHandoffView />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/p/:projectId/workitems"
          element={
            <ProtectedRoute>
              <Layout fullWidth>
                <WorkItemsView />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/p/:projectId/architect/diagram"
          element={
            <ProtectedRoute>
              <Layout>
                <ForgeTechnicalHandoffView />
              </Layout>
            </ProtectedRoute>
          }
        />

        <Route
          path="/repos"
          element={
            <ProtectedRoute>
              <Layout fullWidth>
                <RepoWorkspaceView />
              </Layout>
            </ProtectedRoute>
          }
        />
        <Route path="/admin/settings" element={<ProtectedRoute><Layout><AdminSettingsView /></Layout></ProtectedRoute>} />
        <Route path="/admin/projects" element={<ProtectedRoute><Layout><AdminProjectsView /></Layout></ProtectedRoute>} />
        <Route path="/admin/integrations" element={<ProtectedRoute><Layout><AdminDatasourcesView /></Layout></ProtectedRoute>} />
        <Route path="/admin/gateway" element={<ProtectedRoute><Layout><AdminGatewayView /></Layout></ProtectedRoute>} />
        <Route path="/admin/graph-intelligence" element={<ProtectedRoute><Layout><AdminGraphIntelligenceView /></Layout></ProtectedRoute>} />
        <Route path="/admin" element={<Navigate to="/admin/settings" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

export default function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  )
}
